// src/store/cartSlice.js
import { createSelector, createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [], // Structure: { product: {...}, quantity: number }
};

export const normalizeAvailableQuantity = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const parsedQuantity = Number(value);

  if (!Number.isFinite(parsedQuantity)) {
    return null;
  }

  return Math.max(0, Math.floor(parsedQuantity));
};

export const isCollectionCartProduct = (product) => {
  const productSlug = String(
    product?.real_slug ||
      product?.slug ||
      product?.productSlug ||
      product?.product_url ||
      "",
  ).trim();

  if (productSlug.startsWith("mrk-")) {
    return false;
  }

  const productType =
    product?.productType ||
    product?.product_type;

  if (productType === "product") {
    return true;
  }

  if (productType === "marketplace") {
    return false;
  }

  return product?.is_marketplace === false;
};

export const getCartProductAvailableQuantity = (product) => {
  if (!isCollectionCartProduct(product)) {
    return null;
  }

  return normalizeAvailableQuantity(
    product?.available_quantity ??
      product?.quantity,
  );
};

export const getCartBundleQuantity = (bundle) => {
  const items = Array.isArray(bundle?.items) ? bundle.items : [];

  if (!items.length) return 1;
  const quantity = Number(items[0]?.quantity || 1);
  if (!Number.isFinite(quantity) || quantity < 1) return 1;
  return Math.floor(quantity);
};

export const getCartBundleAvailableQuantity = ( bundle ) => {
  const items = Array.isArray(bundle?.items) ? bundle.items : [];

  if (!items.length) return null;

  const knownQuantities = items.map((item) =>
      getCartProductAvailableQuantity(item?.product)
    ).filter((quantity) => quantity !== null);

  if (!knownQuantities.length) return null;

  return Math.min(...knownQuantities);
};

const hasMatchingProductId = (cartItem, productId) => {
  return (
    Number(cartItem?.product?.id) ===
    Number(productId)
  );
};

const getBundleInstanceId = (item) => String(item?.bundle?.instanceId || item?.bundle?.id || "").trim();

export const buildCartBundleInstanceId = (bundle, items = []) => {
  const bundleId = String(bundle?.id || "").trim();

  if (!bundleId) return "";

  const variantIds = items
    .map((item) =>
      Number(
        item?.product?.variant_id ??
          item?.product?.id,
      ),
    )
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);

  if (!variantIds.length) return bundleId;

  return `${bundleId}::${variantIds.join("-")}`;
};

export const buildCartProductWithVariant = (product, variant) => {
  const variantId = Number(variant?.id);

  if (!Number.isFinite(variantId) || variantId <= 0) return null;

  const originalPrice = variant?.originalPrice ?? variant?.original_price ?? variant?.price ?? product?.original_price ?? product?.originalPrice ?? product?.price ?? 0;
  const discountedPrice = variant?.discountedPrice ?? variant?.discounted_price ?? variant?.price ?? originalPrice;
  const discountPercent = variant?.discountPercent ?? variant?.discount ?? variant?.discount_percent ?? product?.discountPercent ?? product?.discount ?? product?.discount_percent ?? 0;
  const image = variant?.image || product?.image || product?.image_url || "";
  const existingImages = Array.isArray(product?.main_images) ? product.main_images : [];

  return {
    ...product,
    id: variantId,
    variant_id: variantId,
    size: String(variant?.size || "").trim().toUpperCase(),
    color: variant?.color || product?.color || "",
    image,
    image_url: image,
    main_images: image
      ? [image, ...existingImages.filter((item) => item && item !== image)]
      : existingImages,
    available_quantity: variant?.quantity ?? product?.available_quantity ?? product?.quantity,
    quantity: variant?.quantity ?? product?.quantity,
    price: discountedPrice,
    original_price: originalPrice,
    originalPrice,
    discounted_price: discountedPrice,
    discountedPrice,
    discount: discountPercent,
    discount_percent: discountPercent,
    discountPercent,
    variant_original_price: originalPrice,
    variant_discounted_price: discountedPrice,
    variant_discount: discountPercent,
  };
};

export const isExclusiveCartProduct = (product) => product?.productType === "exclusive" || product?.product_type === "exclusive";

const getReservedProductQuantity = (items, productId) =>
  items.reduce((total, item) => {
    if (!hasMatchingProductId(item, productId)) return total;

    return total + Math.max(1, Number(item?.quantity || 1));
  }, 0);

export const groupCartItems = (items = []) => {
  const bundlesByInstanceId = new Map();
  const standaloneItems = [];

  items.forEach((item) => {
    const bundleInstanceId = getBundleInstanceId(item);

    if (!bundleInstanceId) {
      standaloneItems.push(item);
      return;
    }

    if (!bundlesByInstanceId.has(bundleInstanceId)) {
      bundlesByInstanceId.set(bundleInstanceId, {
        ...item.bundle,
        id: String(item?.bundle?.id || "").trim(),
        instanceId: bundleInstanceId,
        items: [],
      });
    }

    bundlesByInstanceId.get(bundleInstanceId).items.push(item);
  });
  const bundles = Array.from(bundlesByInstanceId.values()).map((bundle) => ({
    ...bundle,
    quantity: getCartBundleQuantity(bundle),
  }));

  return {
    bundles,
    standaloneItems,
  };
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const {
        product,
        quantity: requestedQuantity = 1,
      } = action.payload || {};

      if (
        product?.id === null ||
        product?.id === undefined
      ) {
        return;
      }

      const parsedRequestedQuantity =
        Number(requestedQuantity);

      const quantityToAdd =
        Number.isFinite(parsedRequestedQuantity) &&
        parsedRequestedQuantity > 0
          ? Math.floor(parsedRequestedQuantity)
          : 1;

      const incomingAvailableQuantity =
        getCartProductAvailableQuantity(product);

      if (
        incomingAvailableQuantity !== null &&
        incomingAvailableQuantity <= 0
      ) {
        return;
      }

      const existingItem = state.items.find((item) => !getBundleInstanceId(item) &&
        hasMatchingProductId(item, product.id),
      );

      if (existingItem) {
        const currentQuantity = Math.max(
          1,
          Number(existingItem?.quantity || 1),
        );
        const existingAvailableQuantity =
          getCartProductAvailableQuantity(
            existingItem.product,
          );

        const availableQuantity =
          incomingAvailableQuantity ??
          existingAvailableQuantity;
        existingItem.product = {
          ...existingItem.product,
          ...product,
        };

        const nextQuantity =
          currentQuantity + quantityToAdd;

        existingItem.quantity =
          availableQuantity === null
            ? nextQuantity
            : Math.min(
                nextQuantity,
                availableQuantity,
              );

        return;
      }

      const initialQuantity =
        incomingAvailableQuantity === null
          ? quantityToAdd
          : Math.min(
              quantityToAdd,
              incomingAvailableQuantity,
            );

      if (initialQuantity <= 0) {
        return;
      }

      state.items.push({
        product,
        quantity: initialQuantity,
      });
    },

    addBundleToCart: (state, action) => {
      const { bundle, items } = action.payload || {};

      if (!bundle?.id || !Array.isArray(items) || !items.length) return;
 
      const bundleId = String(bundle.id);
      const requestedBundleQuantity = Math.max(1, Math.floor(Number(bundle?.quantity ?? items[0]?.quantity ?? 1)));
      const bundleInstanceId = buildCartBundleInstanceId(bundle, items);
      if (!bundleInstanceId) return;

      const bundleMeta = {
        id: bundleId,
        instanceId: bundleInstanceId,
        name: bundle.name || "Bundle",
        discountPercent: Number(bundle.discountPercent || 0),
      };

      const normalizedItems = [];

      for (const item of items) {
        const product = item?.product;
        if (product?.id === null || product?.id === undefined) return;
        const availableQuantity = getCartProductAvailableQuantity(product);
        const alreadyReserved = getReservedProductQuantity(state.items, product.id);
        if (availableQuantity !== null && alreadyReserved + requestedBundleQuantity > availableQuantity) return;

        normalizedItems.push({
          product,
          quantity: requestedBundleQuantity,
          bundle: bundleMeta,
        });
      }

      const existingBundleItems = state.items.filter(
        (item) =>
          getBundleInstanceId(item) ===
          bundleInstanceId,
      );

      if (existingBundleItems.length) {
        const currentQuantity = Math.max(1, Number(existingBundleItems[0]?.quantity || 1));
        const nextQuantity = currentQuantity + requestedBundleQuantity;
        const incomingByProductId = new Map(
          normalizedItems.map((item) => [
            String(item.product.id),
            item,
          ]),
        );

        existingBundleItems.forEach(
          (existingItem) => {
            const incomingItem =
              incomingByProductId.get(
                String(existingItem.product.id),
              );

            if (incomingItem) {
              existingItem.product = {
                ...existingItem.product,
                ...incomingItem.product,
              };
            }

            existingItem.quantity = nextQuantity;
            existingItem.bundle = bundleMeta;
          },
        );
        return;
      }
      state.items.push(...normalizedItems);
    },

    incrementBundleQuantity: (state, action) => {
      const bundleInstanceId = String(action.payload || "").trim();
      if (!bundleInstanceId) return;

      const bundleItems = state.items.filter((item) => getBundleInstanceId(item) === bundleInstanceId);
      if (!bundleItems.length) return;
      const currentQuantity = Math.max(1,Number(bundleItems[0]?.quantity || 1));

      const nextQuantity = currentQuantity + 1;
      const canIncrement = bundleItems.every((bundleItem) => {
          const availableQuantity = getCartProductAvailableQuantity(bundleItem?.product);
          if (availableQuantity === null) return true;
          const totalReserved = getReservedProductQuantity(state.items, bundleItem?.product?.id);
          return totalReserved + 1 <= availableQuantity;
        });

      if (!canIncrement) return;
      bundleItems.forEach((item) => {
        item.quantity = nextQuantity;
      });
    },

    decrementBundleQuantity: (state, action) => {
      const bundleInstanceId = String(action.payload || "").trim();
      if (!bundleInstanceId) return;
      const bundleItems = state.items.filter((item) => getBundleInstanceId(item) === bundleInstanceId);
      if (!bundleItems.length) return;
      const currentQuantity = Math.max(1, Number(bundleItems[0]?.quantity || 1,));
      if (currentQuantity <= 1) return;
      const nextQuantity = currentQuantity - 1;
      bundleItems.forEach((item) => {
        item.quantity = nextQuantity;
      });
    },
    incrementQuantity: (state, action) => {
      const item = state.items.find((cartItem) =>
      !getBundleInstanceId(cartItem) &&
        hasMatchingProductId(
          cartItem,
          action.payload,
        ),
      );

      if (!item) return;

      const currentQuantity = Math.max(
        1,
        Number(item?.quantity || 1),
      );

      const availableQuantity =
        getCartProductAvailableQuantity(
          item.product,
        );

      if (availableQuantity === null) {
        item.quantity = currentQuantity + 1;
        return;
      }
      if (availableQuantity <= 0) {
        return;
      }
      if (currentQuantity >= availableQuantity) {
        return;
      }

      item.quantity = currentQuantity + 1;
    },
    decrementQuantity: (state, action) => {
      const item = state.items.find((cartItem) =>
        hasMatchingProductId(
          cartItem,
          action.payload,
        ),
      );
      if (!item || item?.bundle?.id) return;

      if (item && Number(item.quantity) > 1) {
        item.quantity -= 1;
      }
    },
    updateStandaloneCartVariant: (state, action) => {
      const {currentVariantId, variant} = action.payload || {};
      const currentId = Number(currentVariantId);
      if (!Number.isFinite(currentId)) return;

      const currentIndex = state.items.findIndex(
        (item) =>
          !getBundleInstanceId(item) &&
          Number(item?.product?.variant_id ?? item?.product?.id) === currentId,
      );

      if (currentIndex < 0) return;
      const currentItem = state.items[currentIndex];
      const nextProduct = buildCartProductWithVariant(currentItem.product,variant);

      if (!nextProduct) return;
      const nextVariantId = Number(nextProduct.variant_id);
      const currentQuantity = Math.max(1, Number(currentItem.quantity || 1));
      const availableQuantity = getCartProductAvailableQuantity(nextProduct);
      if (availableQuantity !== null && currentQuantity > availableQuantity) return;

      const existingTargetIndex = state.items.findIndex(
        (item, index) =>
          index !== currentIndex &&
          !getBundleInstanceId(item) &&
          Number(item?.product?.variant_id ?? item?.product?.id) === nextVariantId,
      );

      if (existingTargetIndex >= 0) {
        const existingTarget = state.items[existingTargetIndex];
        const combinedQuantity = Math.max(1, Number(existingTarget.quantity || 1)) + currentQuantity;
        if (availableQuantity !== null && combinedQuantity > availableQuantity) return;

        existingTarget.product = {
          ...existingTarget.product,
          ...nextProduct,
        };

        existingTarget.quantity = combinedQuantity;
        state.items.splice(currentIndex, 1);
        return;
      }

      currentItem.product = nextProduct;
    },

    updateBundleItemVariant: (state, action) => {
      const { bundleInstanceId, currentVariantId, variant} = action.payload || {};
      const currentBundleInstanceId = String(bundleInstanceId || "").trim();
      if (!currentBundleInstanceId) return;
      const bundleItems = state.items.filter((item) => getBundleInstanceId(item) === currentBundleInstanceId);

      if (!bundleItems.length) return;

      const currentId = Number(currentVariantId);
      const itemToUpdate = bundleItems.find((item) => Number(item?.product?.variant_id ?? item?.product?.id) === currentId);

      if (!itemToUpdate) return;
      const nextProduct = buildCartProductWithVariant(itemToUpdate.product, variant);

      if (!nextProduct) return;
      const bundleQuantity = Math.max(1, Number(bundleItems[0]?.quantity || 1));
      const nextVariantId = Number(nextProduct.variant_id,);
      const availableQuantity = getCartProductAvailableQuantity(nextProduct);

      const reservedOutsideBundle =
        state.items.reduce((total, item) => {
          if (getBundleInstanceId(item) === currentBundleInstanceId) return total;
          const itemVariantId = Number(item?.product?.variant_id ?? item?.product?.id);
          if (itemVariantId !== nextVariantId) return total;
          return (total + Math.max(1, Number(item?.quantity || 1)));
        }, 0);

      if (availableQuantity !== null && reservedOutsideBundle + bundleQuantity > availableQuantity) {
        return;
      }

      const bundleMeta = bundleItems[0]?.bundle;
      if (!bundleMeta) return;

      const previewItems = bundleItems.map((item) =>
        item === itemToUpdate
          ? {
              ...item,
              product: nextProduct,
            }
          : item,
      );

      const nextInstanceId = buildCartBundleInstanceId(bundleMeta, previewItems);
      if (!nextInstanceId) return;

      const bundleCollision = state.items.some((item) =>
          getBundleInstanceId(item) === nextInstanceId &&
          getBundleInstanceId(item) !== currentBundleInstanceId,
      );

      if (bundleCollision) return;
      itemToUpdate.product = nextProduct;

      bundleItems.forEach((item) => {
        item.bundle = {
          ...item.bundle,
          instanceId: nextInstanceId,
        };
      });
    },
    removeFromCart: (state, action) => {
      state.items = state.items.filter(
        (item) =>
          getBundleInstanceId(item) ||
          !hasMatchingProductId(
            item,
            action.payload,
          ),
      );
    },
    removeBundleFromCart: (state, action) => {
      const bundleInstanceId = String(action.payload || "").trim();

      state.items = state.items.filter(
        (item) =>
          getBundleInstanceId(item) !== bundleInstanceId,
      );
    },
    clearCart: (state) => {
      state.items = [];
    },
  },
});

export const {
  addToCart,
  addBundleToCart,
  incrementQuantity,
  decrementQuantity,
  incrementBundleQuantity,
  decrementBundleQuantity,
  updateStandaloneCartVariant,
  updateBundleItemVariant,
  removeFromCart,
  removeBundleFromCart,
  clearCart,
} = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectIsInCart =
  (productId) =>
  (state) =>
    state.cart.items.some((item) =>
      hasMatchingProductId(item, productId),
    );

export const selectCartCount = (state) =>
  state.cart.items.reduce(
    (count, item) =>
      count + Number(item?.quantity || 0),
    0,
  );

export const selectCartBundles = createSelector(
  [selectCartItems],
  (items) => groupCartItems(items).bundles,
);

export const selectStandaloneCartItems = createSelector(
  [selectCartItems],
  (items) => groupCartItems(items).standaloneItems,
);

export const selectHasInvalidStockQuantity = (state) =>
  state.cart.items.some((item) => {
    const availableQuantity =
      getCartProductAvailableQuantity(
        item?.product,
      );

      if (availableQuantity === null) {
      return false;
    }

    const cartQuantity = Number(
      item?.quantity || 1,
    );

    return (
      availableQuantity <= 0 ||
      cartQuantity > availableQuantity
    );
  });

export default cartSlice.reducer;
